// src/services/portfolioService.js

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';

/**
 * Fetch portfolio data from the backend API
 * @returns {Promise<Object>} Portfolio data response
 */
export const fetchPortfolioData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/portfolio`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Add authentication headers if needed
        // 'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error fetching portfolio data:', error);
    throw error;
  }
};

/**
 * Create a new portfolio item
 * @param {Object} portfolioData - Portfolio item data
 * @returns {Promise<Object>} Created portfolio item
 */
export const createPortfolioItem = async (portfolioData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/portfolio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(portfolioData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating portfolio item:', error);
    throw error;
  }
};

/**
 * Update an existing portfolio item
 * @param {string|number} id - Portfolio item ID
 * @param {Object} portfolioData - Updated portfolio data
 * @returns {Promise<Object>} Updated portfolio item
 */
/* export const updatePortfolioItem = async (id, portfolioData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/portfolio/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(portfolioData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating portfolio item:', error);
    throw error;
  }
}; */
export const updatePortfolioItem = async (arId, fields, changedBy, companyName) => {
  const payload = {
    id: arId,                                                          // AR_Id → "id"
    ...(fields.rank !== undefined && { rank: String(fields.rank) }),
    ...(fields.grade !== undefined && { grade: fields.grade }),
    ...(fields.kpi !== undefined && { Top_line_KPI: fields.kpi }),
    ...(fields.kpiValue !== undefined && { Current_KPI_Value_in_Million: fields.kpiValue }),
    ...(fields.news !== undefined && { News: fields.news }),
    ...(fields.annualkpi !== undefined && { Annual_KPI_Value_Forecast_in_Million: fields.annualkpi }),
    ...(fields.revenue !== undefined && { Annual_Revenue_Forecast_in_Million: fields.revenue }),
    ...(fields.cashBalance !== undefined && { Net_Cash_Balance_in_Million: fields.cashBalance }),
    ...(fields.monthlyBurn !== undefined && { Monthly_Burn_in_Million: fields.monthlyBurn }),
    //...(fields.cashOutDate !== undefined && { Cash_Out_Date: fields.cashOutDate }),
    ...(fields.cashOutDate !== undefined && {
      Cash_Out_Date: fields.cashOutDate === "" || fields.cashOutDate === "—"
        ? null  // ← backend expects null, not ""
        : fields.cashOutDate
    }),
    ...(changedBy && { changed_by: changedBy }),
    ...(companyName && { company_name: companyName }),
  };

  const response = await fetch(`${API_BASE_URL}/portfolio`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error(`Update failed: ${response.statusText}`);
  return response.json();
};
/**
 * Delete a portfolio item
 * @param {string|number} id - Portfolio item ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deletePortfolioItem = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/portfolio/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting portfolio item:', error);
    throw error;
  }
};

/**
 * Get a single portfolio item by ID
 * @param {string|number} id - Portfolio item ID
 * @returns {Promise<Object>} Portfolio item details
 */
export const getPortfolioItemById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/portfolio/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching portfolio item:', error);
    throw error;
  }
};

export const togglePortfolioFlag = async (arId, flagValue, changedBy, companyName) => {
  const response = await fetch(`${API_BASE_URL}/portfolio`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: arId,
      Century_Team_Flag: flagValue,
      ...(changedBy && { changed_by: changedBy }),
      ...(companyName && { company_name: companyName }),
    }),
  });
  if (!response.ok) throw new Error(`Flag update failed: ${response.statusText}`);
  return response.json();
};

/**
 * Fetch portfolio history by company name.
 * @param {string} company - Company name (decoded)
 * @returns {Promise<Array>} History entries
 */
export const fetchPortfolioHistory = async (company) => {
  if (!company || !company.trim()) {
    throw new Error("company is required");
  }



  
  try {
    const companyTrimmed = company.trim();
    const params = new URLSearchParams({
      company_name: companyTrimmed,
      company: companyTrimmed,
    });
    const response = await fetch(`${API_BASE_URL}/portfolio/history?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errBody = await response.text();
      throw new Error(errBody || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching portfolio history:", error);
    throw error;
  }
};